export type AppRoute = {
  path: string;
  component?: string;
  redirect?: string;
  hideInMenu?: boolean;
  routes?: AppRoute[];
};

export type ProjectMenuNode = {
  key?: string;
  name?: string;
  icon?: string;
  path?: string;
  isUrl?: boolean;
  target?: string;
  children?: ProjectMenuNode[];
};

export const PROJECT_STORAGE_KEY = 'ab.currentProjectId';
export const PROJECT_ROUTE_PREFIX = '/project';
export const PROJECT_LIST_PATH = '/delivery/project';
export const PROJECT_HOME_PATH_TEMPLATE = '/project/:projectId/project-home';
export const PROJECT_HOME_SUBPATH = '/project-home';

const PROJECT_ROUTE_PARAM = ':projectId';

const LEGACY_PROJECT_STATIC_SEGMENTS = new Set([
  'project-home',
  'project-collection',
  'project-goals',
  'issues',
  'requirements',
  'concept-analysis',
  'application-management',
  'data-modeling',
  'prototype-design',
  'architecture',
  'development-management',
  'code-contribution',
  'setup',
  'agent-management',
  'code-management',
  'api-management',
  'test-management',
  'quality-management',
  'product-input',
  'delivery-planning',
  'task-management',
  'daily-report-management',
  'quote-management',
  'knowledge-base',
  'asset-library',
  'audit-log',
  'project-settings',
]);

const PROJECT_DEFAULT_SUBPATHS: Record<string, string> = {
  'project-dashboard': '/project-home',
  'daily-management': '/task-management',
  issues: '/issues/all',
  'issue-tracker': '/daily-management/issue-tracker/all',
  requirements: '/requirements/overview',
  'requirement-analysis': '/issues/request',
  'concept-analysis': '/concept-analysis/management',
  'application-management': '/application-management/application-analysis',
  'data-modeling': '/data-modeling/modeling-assistant',
  'prototype-design': '/prototype-design/brand-design',
  architecture: '/architecture/overview',
  'architecture-design': '/architecture/overview',
  'development-management': '/development-management/agent-management',
  'development-execution': '/development-execution/application-management',
  'code-management': '/code-management/repositories',
  'api-management': '/api-management/api-list',
  agents: '/agents/workspace',
  'test-management': '/test-management/dashboard',
  'testing-quality': '/testing-quality/overview',
  'quality-management': '/quality-management/quality-analysis',
  deployment: '/deployment/console',
  'application-publication': '/application-publication',
  release: '/release/version-management',
  'knowledge-base': '/knowledge-base/reference-products',
  research: '/research/reference-materials',
  'building-assets': '/building-assets/image-assets',
  'project-assets': '/project-assets/project-documents',
  business: '/business/quotation-management',
  'product-input': '/product-input/dashboard',
  'delivery-planning': '/delivery-planning/story',
  'project-settings': '/project-settings/project-detail',
};

const PROJECT_ALIAS_SUBPATHS: Array<[RegExp, string]> = [
  [/^\/release\/coral-application\/?$/, '/application-publication'],
  [/^\/project-collection\/project-home\/?$/, '/project-home'],
  [/^\/project-collection\/project-goals\/?$/, '/project-goals'],
  [/^\/project-dashboard(\/dashboard)?\/?$/, '/project-home'],
  [/^\/daily-management\/task-management\/?$/, '/task-management'],
  [/^\/requirements\/project-goals\/?$/, '/project-goals'],
  [/^\/requirements\/concept-analysis(\/.*)?$/, '/concept-analysis$1'],
  [/^\/requirement-analysis\/requirement-pool\/?$/, '/issues/request'],
  [
    /^\/requirement-analysis\/high-level-requirements\/?$/,
    '/requirements/overview',
  ],
  [/^\/requirement-analysis\/feature-list\/?$/, '/requirements/feature-list'],
  [/^\/requirement-analysis\/prd\/?$/, '/requirements/document'],
  [/^\/architecture-design\/overview\/?$/, '/architecture/overview'],
  [
    /^\/architecture-design\/data-modeling\/?$/,
    '/data-modeling/modeling-assistant',
  ],
  [/^\/architecture-design\/api-design\/?$/, '/api-management/api-list'],
  [
    /^\/development-execution\/application-management\/?$/,
    '/application-management/application',
  ],
  [/^\/development-execution\/development-tasks\/?$/, '/task-management'],
  [
    /^\/development-execution\/code-management\/?$/,
    '/code-management/repositories',
  ],
  [/^\/development-execution\/code-activity\/?$/, '/code-contribution'],
  [/^\/agents\/project-agents\/?$/, '/development-management/agent-management'],
  [/^\/testing-quality\/overview\/?$/, '/test-management/dashboard'],
  [/^\/testing-quality\/test-cases\/?$/, '/test-management/case'],
  [/^\/testing-quality\/test-execution\/?$/, '/test-management/order'],
  [
    /^\/testing-quality\/quality-analysis\/?$/,
    '/quality-management/quality-analysis',
  ],
  [
    /^\/research\/reference-materials\/?$/,
    '/knowledge-base/reference-materials',
  ],
  [/^\/building-assets\/image-assets\/?$/, '/asset-library'],
  [/^\/project-assets\/deliverables\/?$/, '/asset-library'],
  [/^\/business\/quotation-management\/?$/, '/quote-management'],
  [
    /^\/project-settings\/member-management\/?$/,
    '/project-settings/project-member',
  ],
  [/^\/project-settings\/audit-logs\/?$/, '/audit-log'],
  [
    /^\/project-settings\/project-announcements\/?$/,
    '/project-settings/project-announcement',
  ],
  [/^\/agent-management\/?$/, '/development-management/agent-management'],
  // [/^\/setup\/blueprint\/?$/, '/development-management/blueprint'],
  [
    /^\/data-modeling\/entity-management\/?$/,
    '/data-modeling/modeling-assistant',
  ],
];

const PROJECT_MENU_MODEL: ProjectMenuNode[] = [
  {
    name: 'productionDashboard',
    icon: 'dashboard',
    path: PROJECT_HOME_PATH_TEMPLATE,
  },
  {
    name: 'dailyManagement',
    icon: 'unorderedList',
    path: '/project/:projectId/daily-management',
    children: [
      {
        name: 'taskManagement',
        icon: 'unorderedList',
        path: '/project/:projectId/task-management',
      },
      {
        name: 'communicationRecords',
        icon: 'message',
        path: '/project/:projectId/communication-records',
      },
      {
        name: 'issueTracker',
        icon: 'profileFilled',
        path: '/project/:projectId/issues/all',
      },
      {
        name: 'dailyReportManagement',
        icon: 'fileText',
        path: '/project/:projectId/daily-report-management',
      },
    ],
  },
  {
    name: 'requirementAnalysis',
    icon: 'profile',
    path: '/project/:projectId/requirement-analysis',
    children: [
      {
        name: 'requirementPool',
        icon: 'bulb',
        path: '/project/:projectId/issues/request',
      },
      {
        name: 'structuredAnalysis',
        icon: 'cluster',
        path: '/project/:projectId/requirement-analysis/structured-analysis',
      },
      {
        name: 'highLevelRequirements',
        icon: 'fileSearch',
        path: '/project/:projectId/requirements/overview',
      },
      {
        name: 'featureList',
        icon: 'unorderedList',
        path: '/project/:projectId/requirements/feature-list',
      },
      {
        name: 'prd',
        icon: 'file',
        path: '/project/:projectId/requirements/document',
      },
    ],
  },
  {
    name: 'prototypeDesign',
    icon: 'highlight',
    path: '/project/:projectId/prototype-design',
    children: [
      {
        name: 'brandDesign',
        icon: 'skin',
        path: '/project/:projectId/prototype-design/brand-design',
      },
      {
        name: 'uiGuidelines',
        icon: 'skin',
        path: '/project/:projectId/prototype-design/ui-guidelines',
      },
      {
        name: 'informationArchitecture',
        icon: 'partition',
        path: '/project/:projectId/prototype-design/information-architecture',
      },
      {
        name: 'menuPlanning',
        icon: 'orderedList',
        path: '/project/:projectId/prototype-design/menu-planning',
      },
      {
        name: 'pageDesign',
        icon: 'layout',
        path: '/project/:projectId/prototype-design/page-design',
      },
      {
        name: 'interactionFlow',
        icon: 'branches',
        path: '/project/:projectId/prototype-design/interaction-flow',
      },
      {
        name: 'aiAssistedDesign',
        icon: 'robot',
        path: '/project/:projectId/prototype-design/ai-assisted-design',
      },
      {
        name: 'prototypePreview',
        icon: 'eye',
        path: '/project/:projectId/prototype-design/prototype-preview',
      },
    ],
  },
  {
    name: 'architectureDesign',
    icon: 'cluster',
    path: '/project/:projectId/architecture-design',
    children: [
      {
        name: 'architectureOverview',
        icon: 'eye',
        path: '/project/:projectId/architecture/overview',
      },
      {
        name: 'applicationArchitecture',
        icon: 'appstore',
        path: '/project/:projectId/architecture-design/application-architecture',
      },
      {
        name: 'technicalStandards',
        icon: 'tool',
        path: '/project/:projectId/architecture-design/technical-standards',
      },
      {
        name: 'dataModeling',
        icon: 'database',
        path: '/project/:projectId/data-modeling/modeling-assistant',
      },
      {
        name: 'apiDesign',
        icon: 'api',
        path: '/project/:projectId/api-management/api-list',
      },
      {
        name: 'permissionModel',
        icon: 'safety',
        path: '/project/:projectId/architecture-design/permission-model',
      },
      {
        name: 'integrationDesign',
        icon: 'shareAlt',
        path: '/project/:projectId/architecture-design/integration-design',
      },
      {
        name: 'adr',
        icon: 'audit',
        path: '/project/:projectId/architecture-design/adr',
      },
    ],
  },
  {
    name: 'developmentExecution',
    icon: 'code',
    path: '/project/:projectId/development-execution',
    children: [
      {
        name: 'applicationManagement',
        icon: 'appstore',
        path: '/project/:projectId/application-management/application',
      },
      {
        key: 'development-execution-tasks',
        name: 'developmentTasks',
        icon: 'schedule',
        path: '/project/:projectId/task-management',
      },
      {
        name: 'codeManagement',
        icon: 'code',
        path: '/project/:projectId/code-management/repositories',
      },
      {
        name: 'codeMigration',
        icon: 'branches',
        path: '/project/:projectId/code-management/migration',
      },
      {
        name: 'codeActivity',
        icon: 'branches',
        path: '/project/:projectId/code-contribution',
      },
      {
        name: 'buildRecords',
        icon: 'thunderbolt',
        path: '/project/:projectId/development-execution/build-records',
      },
    ],
  },
  {
    name: 'agentsManagement',
    icon: 'robot',
    path: '/project/:projectId/agents',
    children: [
      {
        name: 'agentWorkspace',
        icon: 'robot',
        path: '/project/:projectId/agents/workspace',
      },
      {
        name: 'projectAgents',
        icon: 'team',
        path: '/project/:projectId/development-management/agent-management',
      },
    ],
  },
  {
    name: 'testingQuality',
    icon: 'bug',
    path: '/project/:projectId/testing-quality',
    children: [
      {
        name: 'testingOverview',
        icon: 'dashboard',
        path: '/project/:projectId/test-management/dashboard',
      },
      {
        name: 'testPlan',
        icon: 'schedule',
        path: '/project/:projectId/testing-quality/test-plan',
      },
      {
        name: 'testCases',
        icon: 'checkSquare',
        path: '/project/:projectId/test-management/case',
      },
      {
        name: 'testExecution',
        icon: 'orderedList',
        path: '/project/:projectId/test-management/order',
      },
      {
        name: 'qualityAnalysis',
        icon: 'safety',
        path: '/project/:projectId/quality-management/quality-analysis',
      },
      {
        name: 'acceptanceManagement',
        icon: 'fileDone',
        path: '/project/:projectId/testing-quality/acceptance-management',
      },
    ],
  },
  {
    name: 'deploymentOperations',
    icon: 'cloud',
    path: '/project/:projectId/deployment',
    children: [
      {
        name: 'console',
        icon: 'rocket',
        path: '/project/:projectId/deployment/console',
      },
      {
        name: 'pipelines',
        icon: 'branches',
        path: '/project/:projectId/deployment/pipelines',
      },
      {
        name: 'runtime',
        icon: 'dashboard',
        path: '/project/:projectId/deployment/runtime',
      },
      {
        name: 'coralApplicationPublication',
        icon: 'shop',
        path: '/project/:projectId/application-publication',
      },
    ],
  },
  {
    name: 'releaseDelivery',
    icon: 'rocket',
    path: '/project/:projectId/release',
    children: [
      {
        name: 'versionManagement',
        icon: 'tags',
        path: '/project/:projectId/release/version-management',
      },
      {
        name: 'releasePlan',
        icon: 'schedule',
        path: '/project/:projectId/release/release-plan',
      },
      {
        name: 'releaseRecords',
        icon: 'audit',
        path: '/project/:projectId/release/release-records',
      },
      {
        name: 'deliverablesManagement',
        icon: 'folder',
        path: '/project/:projectId/release/deliverables-management',
      },
    ],
  },
  {
    name: 'researchReferences',
    icon: 'book',
    path: '/project/:projectId/research',
    children: [
      {
        name: 'referenceMaterials',
        icon: 'folder',
        path: '/project/:projectId/knowledge-base/reference-materials',
      },
      {
        name: 'competitorManagement',
        icon: 'trophy',
        path: '/project/:projectId/research/competitor-management',
      },
      {
        name: 'competitorAnalysis',
        icon: 'barChart',
        path: '/project/:projectId/research/competitor-analysis',
      },
      {
        name: 'competitorAnalysisReport',
        icon: 'fileText',
        path: '/project/:projectId/research/competitor-analysis-report',
      },
    ],
  },
  {
    name: 'buildingAssets',
    icon: 'picture',
    path: '/project/:projectId/building-assets',
    children: [
      {
        name: 'imageAssets',
        icon: 'picture',
        path: '/project/:projectId/asset-library',
      },
      {
        name: 'iconAssets',
        icon: 'star',
        path: '/project/:projectId/building-assets/icon-assets',
      },
    ],
  },
  {
    name: 'projectAssets',
    icon: 'folderOpen',
    path: '/project/:projectId/project-assets',
    children: [
      {
        name: 'projectDocuments',
        icon: 'fileText',
        path: '/project/:projectId/project-assets/project-documents',
      },
      {
        key: 'project-assets-deliverables',
        name: 'deliverables',
        icon: 'fileDone',
        path: '/project/:projectId/asset-library',
      },
    ],
  },
  {
    name: 'businessManagement',
    icon: 'fileDone',
    path: '/project/:projectId/business',
    children: [
      {
        name: 'quotationManagement',
        icon: 'calculator',
        path: '/project/:projectId/quote-management',
      },
      {
        name: 'contractInformation',
        icon: 'file',
        path: '/project/:projectId/business/contract-information',
      },
      {
        name: 'paymentPlan',
        icon: 'schedule',
        path: '/project/:projectId/business/payment-plan',
      },
      {
        name: 'changeQuotation',
        icon: 'branches',
        path: '/project/:projectId/business/change-quotation',
      },
    ],
  },
  {
    name: 'projectSettings',
    icon: 'setting',
    path: '/project/:projectId/project-settings',
    children: [
      {
        name: 'projectDetail',
        icon: 'profile',
        path: '/project/:projectId/project-settings/project-detail',
      },
      {
        name: 'memberManagement',
        icon: 'team',
        path: '/project/:projectId/project-settings/project-member',
      },
      {
        name: 'auditLogs',
        icon: 'audit',
        path: '/project/:projectId/audit-log',
      },
      {
        name: 'projectAnnouncements',
        icon: 'notification',
        path: '/project/:projectId/project-settings/project-announcement',
      },
      {
        name: 'developerOptions',
        icon: 'tool',
        path: '/project/:projectId/project-settings/developer-options',
      },
    ],
  },
];

const PROJECT_TOP_LEVEL_LEAF_PATHS = new Set<string>();

PROJECT_MENU_MODEL.forEach((item) => {
  if (item.path && !item.children) {
    PROJECT_TOP_LEVEL_LEAF_PATHS.add(item.path);
  }
});

export const PROJECT_OBJECT_CANONICAL_ROUTES: AppRoute[] = [
  {
    path: '/project/:projectId/project-home',
    component: './project-home',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/task-management',
    component: './task-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/communication-records',
    component: './communication-records',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/project-goals',
    component: './requirements/project-goals',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/product-input/dashboard',
    component: './product-input/dashboard',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/product-input/user-feedback',
    component: './product-input/user-feedback',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/issues/all',
    component: './issues',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/issues/bug',
    component: './issues/bug',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/issues/story',
    component: './issues/story',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/issues/feature',
    component: './issues/feature',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/issues/improvement',
    component: './issues/improvement',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/issues/request',
    component: './issues/request',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/issues/confirmation',
    component: './issues/confirmation',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/issues/idea',
    component: './issues/idea',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/issues/note',
    component: './issues/note',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/delivery-planning/estimation-standards',
    component: './delivery-planning/estimation-standards',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/delivery-planning/story',
    redirect: '/project/:projectId/issues/story',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/delivery-planning/task-list-generation',
    component: './delivery-planning/task-list-generation',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/requirements/overview',
    component: './requirements/overview',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/requirements/overview-template-management',
    component: './requirements/overview-template-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/requirements/feature-list',
    component: './requirements/feature-list',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/requirements/role-analysis',
    component: './requirements/role-analysis',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/requirements/system-integration',
    component: './requirements/system-integration',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/requirements/document',
    component: './requirements/document',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/concept-analysis/management',
    component: './requirements/concept-analysis/management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/concept-analysis/group',
    component: './requirements/concept-analysis/group',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/concept-analysis/relation',
    component: './requirements/concept-analysis/relation',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/application-management/application-analysis',
    component: './requirements/application-analysis',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/application-management/application',
    component: './application',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/data-modeling/modeling-assistant',
    component: './data-modeling/modeling-assistant',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/data-modeling/rule-management',
    component: './data-modeling/rule-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/data-modeling/data-source-management',
    component: './data-modeling/data-source-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/ai-assisted-design',
    component: './prototype-design/ai-assisted-design',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/theme-design',
    component: './prototype-design/theme-design',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/statistical-analysis-design',
    component: './prototype-design/design-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/big-screen-design',
    component: './prototype-design/design-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/design-management',
    component: './prototype-design/design-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/architecture/overview',
    component: './architecture/overview',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/architecture/detail',
    component: './architecture/detail',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/development-management/agent-management',
    component: './project/agent-management',
    hideInMenu: true,
  },
  // {
  //   path: '/project/:projectId/development-management/dev-agent-v1-planning',
  //   component: './project/dev-agent-v1-planning',
  //   hideInMenu: true,
  // },
  // {
  //   path: '/project/:projectId/development-management/dev-agent-v1',
  //   component: './project/dev-agent-v1',
  //   hideInMenu: true,
  // },
  // {
  //   path: '/project/:projectId/development-management/dev-agent-v1/tasks/:taskUuid/workspace',
  //   component: './project/dev-agent-v1-workspace',
  //   hideInMenu: true,
  // },
  // {
  //   path: '/project/:projectId/development-management/blueprint',
  //   component: './project/setup/blueprint',
  //   hideInMenu: true,
  // },
  {
    path: '/project/:projectId/code-management/repositories',
    component: './code-management/repositories',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/code-management/migration',
    component: './code-management/migration',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/code-management/dependency-analysis',
    component: './code-management/dependency-analysis',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/api-management/api-list',
    component: './architecture/api-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/api-management/quick-debug',
    component: './api-management/quick-debug',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/code-contribution',
    component: './project/code-contribution',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/test-management/dashboard',
    component: './test-management/dashboard',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/test-management/bug',
    component: './test-management/bug',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/test-management/case',
    component: './test-management/case',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/test-management/suite',
    component: './test-management/suite',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/test-management/order',
    component: './test-management/order',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/test-management/report',
    component: './test-management/report',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/test-management/case-library',
    component: './test-management/case-library',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/quality-management/quality-analysis',
    component: './quality-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/daily-report-management',
    component: './daily-report-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/quote-management',
    component: './quote-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/knowledge-base/reference-products',
    component: './knowledge-base/reference-products',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/knowledge-base/reference-materials',
    component: './knowledge-base/reference-materials',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/asset-library',
    component: './asset-library',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/audit-log',
    component: './audit-log',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/project-settings/project-detail',
    component: './project-settings/project-detail',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/project-settings/project-member',
    component: './project-settings/project-member',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/project-settings/project-collaboration',
    component: './project-settings/project-collaboration',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/project-settings/project-announcement',
    component: './project-settings/project-announcement',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/project-settings/project-tags',
    component: './project-settings/project-tags',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/project-settings/developer-options',
    component: './project-settings/developer-options',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/requirement-analysis/structured-analysis',
    component: './project-workspace/requirement/structured-analysis',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/brand-design',
    component: './project-workspace/prototype/brand-design',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/ui-guidelines',
    component: './project-workspace/prototype/ui-guidelines',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/information-architecture',
    component: './project-workspace/prototype/information-architecture',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/menu-planning',
    component: './project-workspace/prototype/menu-planning',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/page-design',
    component: './project-workspace/prototype/page-design',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/interaction-flow',
    component: './project-workspace/prototype/interaction-flow',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/ai-assisted-design',
    component: './prototype-design/ai-assisted-design',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/prototype-design/prototype-preview',
    component: './project-workspace/prototype/prototype-preview',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/architecture-design/application-architecture',
    component: './project-workspace/architecture/application-architecture',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/architecture-design/technical-standards',
    component: './project-workspace/architecture/technical-standards',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/architecture-design/permission-model',
    component: './project-workspace/architecture/permission-model',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/architecture-design/integration-design',
    component: './project-workspace/architecture/integration-design',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/architecture-design/adr',
    component: './project-workspace/architecture/adr',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/development-execution/build-records',
    component: './project-workspace/development/build-records',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/agents/workspace',
    component: './project-workspace/agents/workspace',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/testing-quality/test-plan',
    component: './project-workspace/testing/test-plan',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/testing-quality/acceptance-management',
    component: './project-workspace/testing/acceptance-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/deployment/console',
    component: './project-workspace/deployment/console',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/deployment/pipelines',
    component: './project-workspace/deployment/pipelines',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/deployment/runtime',
    component: './project-workspace/deployment/runtime',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/release/version-management',
    component: './project-workspace/release/version-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/release/release-plan',
    component: './project-workspace/release/release-plan',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/release/release-records',
    component: './project-workspace/release/release-records',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/release/deliverables-management',
    component: './project-workspace/release/deliverables-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/release/coral-application',
    component: './project-workspace/release/coral-application/LegacyRedirect',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/application-publication',
    component: './project-workspace/release/coral-application',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/research/competitor-management',
    component: './project-workspace/research/competitor-management',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/research/competitor-analysis',
    component: './project-workspace/research/competitor-analysis',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/research/competitor-analysis-report',
    component: './project-workspace/research/competitor-analysis-report',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/building-assets/icon-assets',
    component: './project-workspace/building-assets/icon-assets',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/project-assets/project-documents',
    component: './project-workspace/project-assets/project-documents',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/business/contract-information',
    component: './project-workspace/business/contract-information',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/business/payment-plan',
    component: './project-workspace/business/payment-plan',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/business/change-quotation',
    component: './project-workspace/business/change-quotation',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/daily-management/issue-tracker/risk',
    component: './project-workspace/daily/issue-tracker/risk',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/daily-management/issue-tracker/problem',
    component: './project-workspace/daily/issue-tracker/problem',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/daily-management/issue-tracker/change-request',
    component: './project-workspace/daily/issue-tracker/change-request',
    hideInMenu: true,
  },
  {
    path: '/project/:projectId/daily-management/issue-tracker/pending-confirmation',
    component: './project-workspace/daily/issue-tracker/pending-confirmation',
    hideInMenu: true,
  },
];

const normalizeProjectSubpath = (subpath?: string): string => {
  const raw = `/${(subpath || '')
    .trim()
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')}`;

  let normalized = raw === '/' ? PROJECT_HOME_SUBPATH : raw;
  for (const [pattern, replacement] of PROJECT_ALIAS_SUBPATHS) {
    if (pattern.test(normalized)) {
      normalized = normalized.replace(pattern, replacement);
      break;
    }
  }

  const firstSegment = normalized.split('/').filter(Boolean)[0];
  if (
    firstSegment &&
    PROJECT_DEFAULT_SUBPATHS[firstSegment] &&
    normalized === `/${firstSegment}`
  ) {
    return PROJECT_DEFAULT_SUBPATHS[firstSegment];
  }

  return normalized;
};

const getStoredProjectId = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.localStorage.getItem(PROJECT_STORAGE_KEY) || '';
};

export const isProjectRoute = (pathname: string) =>
  pathname === PROJECT_ROUTE_PREFIX ||
  pathname.startsWith(`${PROJECT_ROUTE_PREFIX}/`);

export const isProjectObjectPath = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'project') {
    return false;
  }

  const candidate = segments[1];
  return Boolean(candidate && !LEGACY_PROJECT_STATIC_SEGMENTS.has(candidate));
};

export const extractProjectIdFromPathname = (pathname: string): string => {
  if (!isProjectObjectPath(pathname)) {
    return '';
  }

  return pathname.split('/').filter(Boolean)[1] || '';
};

export const fillProjectPath = (pathTemplate: string, projectId: string) =>
  pathTemplate.replace(PROJECT_ROUTE_PARAM, projectId);

export const getProjectHomePath = (projectId: string) =>
  fillProjectPath(PROJECT_HOME_PATH_TEMPLATE, projectId);

export const buildProjectMenuData = (projectId: string): ProjectMenuNode[] => {
  const mapNode = (item: ProjectMenuNode): ProjectMenuNode => {
    const path =
      item.path && projectId && item.path.startsWith(PROJECT_ROUTE_PREFIX)
        ? fillProjectPath(item.path, projectId)
        : item.path;

    return {
      ...item,
      path,
      children: item.children?.map(mapNode),
    };
  };

  return PROJECT_MENU_MODEL.map(mapNode);
};

export const isTopLevelProjectMenuPath = (pathname: string) => {
  if (!isProjectObjectPath(pathname)) {
    return false;
  }

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 2) {
    return false;
  }

  const normalizedPath = `/${segments
    .map((segment, index) =>
      index === 1 && segments[0] === 'project' ? PROJECT_ROUTE_PARAM : segment,
    )
    .join('/')}`;

  return PROJECT_TOP_LEVEL_LEAF_PATHS.has(normalizedPath);
};

export const buildProjectPath = (projectId: string, targetPath: string) => {
  if (!targetPath.startsWith(PROJECT_ROUTE_PREFIX)) {
    return targetPath;
  }

  if (targetPath.includes(PROJECT_ROUTE_PARAM)) {
    return fillProjectPath(targetPath, projectId);
  }

  if (isProjectObjectPath(targetPath)) {
    const segments = targetPath.split('/').filter(Boolean);
    const [, , ...rest] = segments;
    return `${PROJECT_ROUTE_PREFIX}/${projectId}${rest.length > 0 ? `/${rest.join('/')}` : ''}`;
  }

  const subpath = normalizeProjectSubpath(
    targetPath.slice(PROJECT_ROUTE_PREFIX.length),
  );
  return `${PROJECT_ROUTE_PREFIX}/${projectId}${subpath}`;
};

export const replaceProjectIdInPath = (pathname: string, projectId: string) => {
  if (!isProjectObjectPath(pathname)) {
    return getProjectHomePath(projectId);
  }

  const segments = pathname.split('/').filter(Boolean);
  const rest = segments.slice(2).join('/');
  const subpath = normalizeProjectSubpath(rest);
  return `${PROJECT_ROUTE_PREFIX}/${projectId}${subpath}`;
};

export const getProjectIdFromLocation = (pathname: string, search = '') => {
  const fromPath = extractProjectIdFromPathname(pathname);
  if (fromPath) {
    return fromPath;
  }

  const fromQuery = new URLSearchParams(search).get('projectId');
  if (fromQuery) {
    return fromQuery;
  }

  return getStoredProjectId();
};

export const getCanonicalProjectPath = (
  pathname: string,
  projectId: string,
) => {
  if (!projectId) {
    return '';
  }

  if (pathname === PROJECT_ROUTE_PREFIX) {
    return getProjectHomePath(projectId);
  }

  if (isProjectObjectPath(pathname)) {
    const objectPrefix = `${PROJECT_ROUTE_PREFIX}/${projectId}`;
    const subpath = normalizeProjectSubpath(
      pathname.slice(objectPrefix.length),
    );
    return `${PROJECT_ROUTE_PREFIX}/${projectId}${subpath}`;
  }

  if (!pathname.startsWith(`${PROJECT_ROUTE_PREFIX}/`)) {
    return '';
  }

  const subpath = normalizeProjectSubpath(
    pathname.slice(PROJECT_ROUTE_PREFIX.length),
  );
  return `${PROJECT_ROUTE_PREFIX}/${projectId}${subpath}`;
};

export const stripProjectQueryParam = (search: string) => {
  const params = new URLSearchParams(search);
  params.delete('projectId');
  const next = params.toString();
  return next ? `?${next}` : '';
};
